import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Grid, Snackbar, Alert, Dialog, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BuildingDetailsAppliancesAddAppliance from './BuildingDetailsAppliancesAddAppliance';

const BuildingDetailsAppliances = () => {
    const { id } = useParams();
    const [appliances, setAppliances] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);
    const [editAppliance, setEditAppliance] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    const fetchAppliances = () => {
        axios.get(`/buildings/${id}/appliances/`)
            .then(res => setAppliances(res.data))
            .catch(() => setSnackbar({ open: true, message: 'Failed to fetch appliances.', severity: 'error' }));
    };

    useEffect(() => {
        fetchAppliances();
    }, [id]);

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => {
        setModalOpen(false);
        setEditAppliance(null);
    };

    const handleSuccess = () => {
        fetchAppliances();
        const message = editAppliance
            ? 'Appliance updated successfully!'
            : 'Appliance added successfully!';
        setSnackbar({ open: true, message, severity: 'success' });
    };

    const handleError = () => {
        setSnackbar({ open: true, message: 'Failed to save appliance.', severity: 'error' });
    };

    const handleDelete = (applianceId) => {
        axios.delete(`/buildings/${id}/appliances/${applianceId}/`)
            .then(() => {
                fetchAppliances();
                setSnackbar({ open: true, message: 'Appliance deleted successfully!', severity: 'success' });
            })
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to delete appliance.', severity: 'error' });
            });
    };


    return (
        <Box sx={{ my: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Appliances</Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleModalOpen}>
                    Add Appliance
                </Button>
            </Box>

            {appliances
                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                .map((appliance) => (
                    <Accordion
                        key={appliance.id}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            boxShadow: 'none',
                            border: '1px solid #e0e0e0',
                            '&::before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                                {appliance.appliance_type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditAppliance(appliance);
                                    setModalOpen(true);
                                }}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{ mr: 2 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(appliance.id);
                                }}
                            >
                                Delete
                            </Button>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {Object.entries(appliance).map(([key, value]) => {
                                    if (
                                        key === 'id' || key === 'building' ||
                                        value === null || value === undefined || value === ''
                                    ) return null;

                                    const label = key
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, char => char.toUpperCase());

                                    // Format value if it's appliance_type
                                    const formattedValue = key === 'appliance_type'
                                        ? value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                        : value;

                                    return (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Typography variant="subtitle2">{label}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formattedValue}</Typography>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={Math.ceil(appliances.length / itemsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                />
            </Box>

            <Dialog open={modalOpen} onClose={handleModalClose}>
                <BuildingDetailsAppliancesAddAppliance
                    buildingId={id}
                    appliance={editAppliance}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    onClose={handleModalClose}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BuildingDetailsAppliances;
